import {
  animate,
  state,
  style,
  transition,
  trigger
} from "@angular/animations";
import { coerceBooleanProperty } from "@angular/cdk/coercion";
import { DOCUMENT } from "@angular/common";
import { Component, EventEmitter, Inject, Input, Output } from "@angular/core";
import { ActivatedRoute, NavigationEnd, Router } from "@angular/router";
import { Observable } from "rxjs";
import { filter, map } from "rxjs/operators";

import { AppConfig, AuthService } from "@lantern/app/core";
import { logoTextMap } from "./logo-text";

const ANIMATION_TIME_MS = 200;

@Component({
  selector: "ta-site-nav",
  templateUrl: "./site-nav.template.html",
  styles: [
    `
      [min-content-width] {
        min-width: 960px;
      }
    `
  ],
  animations: [
    trigger("fadeInOut", [
      state("void", style({ opacity: 0 })),
      state("*", style({ opacity: 1 })),
      transition("void <=> *", [animate(ANIMATION_TIME_MS)])
    ]),
    trigger("horizontalSlideInOut", [
      transition("void => *", [
        style({ transform: "translateX(-40%)" }),
        animate(ANIMATION_TIME_MS)
      ]),
      transition("* => void", [
        animate(ANIMATION_TIME_MS, style({ transform: "translateX(-40%)" }))
      ])
    ]),
    trigger("verticalShrink", [
      state("void", style({ height: 0 })),
      state("*", style({ height: "25px" })),
      transition("void <=> *", [animate(ANIMATION_TIME_MS)])
    ])
  ]
})
export class SiteNavComponent {
  @Input()
  set hideMenuButton(value: boolean) {
    this._hideMenuButton = coerceBooleanProperty(value);
  }
  get hideMenuButton(): boolean {
    return this._hideMenuButton;
  }
  _hideMenuButton = false;

  @Input()
  set logoLinksToTapadHomepage(value: boolean) {
    this._logoLinksToTapadHomepage = coerceBooleanProperty(value);
  }
  get logoLinksToTapadHomepage(): boolean {
    return this._logoLinksToTapadHomepage;
  }
  _logoLinksToTapadHomepage = false;

  displayName: string = this.appConfig.displayName;
  backLocation$: Observable<string | undefined> = this.router.events.pipe(
    filter((e) => e instanceof NavigationEnd),
    map(() => {
      let route = this.route.snapshot;
      while (route.firstChild != null) {
        route = route.firstChild;
      }

      while (route.data.backLocation == null && route.parent != null) {
        route = route.parent;
      }
      return route.data.backLocation;
    })
  );

  get logoText(): string {
    const url = new URL(this.document.location.href);
    const logoText = logoTextMap[url.hostname];
    return logoText || this.displayName;
  }

  constructor(
    private appConfig: AppConfig,
    private auth: AuthService,
    private router: Router,
    private route: ActivatedRoute,
    @Inject(DOCUMENT) private document: Document
  ) {}

  get visible(): boolean {
    return this._visible;
  }
  set visible(visible: boolean) {
    this._visible = visible;
    this.visibleChange.emit(visible);
  }
  private _visible: boolean = false;
  @Output() visibleChange: EventEmitter<boolean> = new EventEmitter<boolean>();

  get nav(): string {
    return this._nav;
  }
  set nav(nav: string) {
    this._nav = nav;
    this.navChange.emit(nav);
  }
  private _nav: string = "";
  @Output() navChange: EventEmitter<string> = new EventEmitter<string>();

  toggleNav(nav: string, visible?: boolean): void {
    if (nav !== this.nav) {
      this.visible = true;
    } else {
      this.visible = visible !== undefined ? visible : !this.visible;
    }

    this.nav = nav;
  }

  hideNav(): void {
    this.nav = "";
    this.visible = false;
  }

  logout(): void {
    this.hideNav();
    this.auth.logout().subscribe(() => null);
  }
}
